// Typed client for the /api functions, including the Server-Sent Events adaptation stream.
import type { Access, Adaptation, AdaptParams, BillingConfig, Favorite, Me, Profile, RecommendResult } from "./types";
import type { Lesson, Principle, Rubric } from "../../shared/catalog";

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public data: Record<string, unknown> = {}) {
    super(message);
  }
}

async function request<T>(path: string, init: { method?: string; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: init.method ?? "GET",
      headers: init.body !== undefined ? { "content-type": "application/json" } : undefined,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      credentials: "same-origin",
      signal: init.signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, "OFFLINE", "Can't reach the server. Check your connection and try again.");
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string } }).error ?? {};
    throw new ApiError(res.status, err.code ?? "HTTP_" + res.status, err.message ?? `Request failed (${res.status}).`, err as Record<string, unknown>);
  }
  return data as T;
}

export const api = {
  me: () => request<Me>("/api/me"),
  updateMe: (body: { displayName?: string; prefs?: Record<string, unknown> }) => request<Me>("/api/me", { method: "PATCH", body }),
  deleteAccount: () => request<{ ok: true }>("/api/me", { method: "DELETE" }),
  requestLogin: (email: string, next?: string) => request<{ ok: true }>("/api/auth/request", { method: "POST", body: { email, next } }),
  verify: (body: { token: string } | { email: string; code: string }) => request<{ user: Me["user"] }>("/api/auth/verify", { method: "POST", body }),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  favorites: () => request<{ favorites: Favorite[] }>("/api/favorites"),
  addFavorite: (lessonId: string, note = "") => request("/api/favorites/" + lessonId, { method: "PUT", body: { note } }),
  removeFavorite: (lessonId: string) => request("/api/favorites/" + lessonId, { method: "DELETE" }),

  adaptations: (scope: "saved" | "recent") => request<{ adaptations: Adaptation[] }>("/api/adaptations?scope=" + scope),
  adaptation: (id: string) => request<{ adaptation: Adaptation }>("/api/adaptations/" + id),
  updateAdaptation: (id: string, body: { saved?: boolean; title?: string; share?: boolean }) =>
    request<{ adaptation: Adaptation }>("/api/adaptations/" + id, { method: "PATCH", body }),
  deleteAdaptation: (id: string) => request("/api/adaptations/" + id, { method: "DELETE" }),
  shared: (slug: string) => request<{ adaptation: Adaptation; preview: boolean }>("/api/shared/" + slug),
  lesson: (id: string) => request<{ lesson: Lesson; preview: boolean }>("/api/lessons/" + id),
  handbookContent: () => request<{ principles: Principle[]; rubrics: Rubric[] }>("/api/handbook"),

  profiles: () => request<{ profiles: Profile[] }>("/api/profiles"),
  createProfile: (name: string, params: Profile["params"]) => request<{ profile: Profile }>("/api/profiles", { method: "POST", body: { name, params } }),
  updateProfile: (id: string, name: string, params: Profile["params"]) =>
    request<{ profile: Profile }>("/api/profiles/" + id, { method: "PUT", body: { name, params } }),
  deleteProfile: (id: string) => request("/api/profiles/" + id, { method: "DELETE" }),

  recommend: (query: string) => request<RecommendResult & { model: string }>("/api/recommend", { method: "POST", body: { query } }),
  emailLesson: (body: { lessonId: string; duration?: number } | { adaptationId: string }) =>
    request<{ ok: true; to: string }>("/api/email", { method: "POST", body }),

  billingConfig: () => request<BillingConfig>("/api/billing/config"),
  subscribe: (plan: "monthly" | "yearly", policyVersion: string) =>
    request<{ subscriptionId: string }>("/api/billing/subscribe", { method: "POST", body: { plan, acceptTerms: true, policyVersion } }),
  activate: (subscriptionId: string) => request<{ access: Access; pending: boolean }>("/api/billing/activate", { method: "POST", body: { subscriptionId } }),
  cancelSubscription: () => request<{ access: Access }>("/api/billing/cancel", { method: "POST" }),
};

export interface StreamHandlers {
  onMeta?: (m: { cached: boolean; model: string }) => void;
  onDelta?: (text: string) => void;
  signal?: AbortSignal;
}

/** POST /api/adapt and consume its SSE stream. Resolves with the saved adaptation. */
export async function streamAdapt(lessonId: string, params: AdaptParams, h: StreamHandlers = {}) {
  let res: Response;
  try {
    res = await fetch("/api/adapt", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ lessonId, params }),
      credentials: "same-origin",
      signal: h.signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, "OFFLINE", "Can't reach the server. Check your connection and try again.");
  }
  if (!res.ok || !(res.headers.get("content-type") ?? "").includes("text/event-stream")) {
    const data = (await res.json().catch(() => ({}))) as { error?: { code?: string; message?: string } };
    const err = data.error ?? {};
    throw new ApiError(res.status, err.code ?? "HTTP_" + res.status, err.message ?? "The adaptation couldn't start.", err as Record<string, unknown>);
  }
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let result: { adaptation: Adaptation; remaining: number; cached: boolean } | null = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf("\n\n")) >= 0) {
      const block = buf.slice(0, i);
      buf = buf.slice(i + 2);
      const event = /^event: (.+)$/m.exec(block)?.[1];
      const raw = /^data: (.+)$/m.exec(block)?.[1];
      if (!event || !raw) continue;
      const data = JSON.parse(raw);
      if (event === "meta") h.onMeta?.(data);
      else if (event === "delta") h.onDelta?.(data.text);
      else if (event === "done") result = data;
      else if (event === "error") throw new ApiError(502, data.code, data.message);
    }
  }
  if (!result) throw new ApiError(502, "STREAM_ENDED", "The connection closed before the lesson arrived. Try again — it wasn't counted.");
  return result;
}
